import React from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDiscord,
  faFacebook,
  faInstagram,
  faLinkedin,
  faReddit,
  faTiktok,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import './AppFooter.css';

type FooterRouteLink = {
  label: string;
  to: string;
};

type FooterExternalLink = {
  label: string;
  href: string;
};

type FooterLink = FooterRouteLink | FooterExternalLink;

type FooterGroup = {
  title: string;
  links: FooterLink[];
};

type SocialLink = {
  label: string;
  href: string;
  icon: IconDefinition;
  platform: string;
};

type AppFooterProps = {
  showGettingStartedLink?: boolean;
};

const baseFooterGroups: FooterGroup[] = [
  {
    title: 'Product',
    links: [
      { label: 'AI Agent', to: '/ai-agent' },
      { label: 'Strategy Marketplace', to: '/marketplace' },
      { label: 'MCP for Agents', to: '/agents' },
      { label: 'Plans and Pricing', to: '/pricing' },
      { label: 'Enterprise Services', to: '/enterprise' },
    ],
  },
  {
    title: 'Learn',
    links: [
      { label: 'Case Studies', to: '/case-studies' },
      { label: 'AI Bootcamp', to: '/courses/ai-trading-bootcamp' },
      { label: 'Live Algo Lab', to: '/live-algo-lab' },
      { label: 'Platform Comparisons', to: '/compare' },
      { label: 'Trading Guides', to: '/guides' },
      { label: 'Blog', to: '/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Affiliate Program', to: '/affiliate' },
      { label: 'Terms of Service', to: '/terms' },
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'contact@botspot.trade', href: 'mailto:contact@botspot.trade' },
    ],
  },
];

const getFooterGroups = (showGettingStartedLink: boolean): FooterGroup[] => {
  if (!showGettingStartedLink) {
    return baseFooterGroups;
  }

  return baseFooterGroups.map((group) => {
    if (group.title !== 'Product') {
      return group;
    }

    return {
      ...group,
      links: [
        ...group.links.slice(0, 2),
        { label: 'Getting Started', to: '/onboarding' },
        ...group.links.slice(2),
      ],
    };
  });
};

const socialLinks: SocialLink[] = [
  { label: 'Reddit Community', href: 'https://www.reddit.com/r/BotSpotTrade/', icon: faReddit, platform: 'reddit' },
  { label: 'Discord Chat', href: 'https://discord.gg/4R9j6T3PN8', icon: faDiscord, platform: 'discord' },
  { label: 'YouTube', href: 'https://www.youtube.com/@lumiwealth', icon: faYoutube, platform: 'youtube' },
  { label: 'Instagram', href: 'https://www.instagram.com/lumiwealth', icon: faInstagram, platform: 'instagram' },
  { label: 'TikTok', href: 'https://www.tiktok.com/@robertgrzesiklumi', icon: faTiktok, platform: 'tiktok' },
  { label: 'Facebook', href: 'https://www.facebook.com/share/17dqSWa9LM/', icon: faFacebook, platform: 'facebook' },
  { label: 'X', href: 'https://twitter.com/lumiwealth', icon: faXTwitter, platform: 'x' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/lumiwealth', icon: faLinkedin, platform: 'linkedin' },
];

function isRouteLink(link: FooterLink): link is FooterRouteLink {
  return 'to' in link;
}

function FooterNavLink({ link }: { link: FooterLink }): React.ReactElement {
  if (isRouteLink(link)) {
    return <Link to={link.to}>{link.label}</Link>;
  }

  return (
    <a href={link.href} target={link.href.startsWith('mailto:') ? undefined : '_blank'} rel={link.href.startsWith('mailto:') ? undefined : 'noreferrer'}>
      {link.label}
    </a>
  );
}

export default function AppFooter({ showGettingStartedLink = false }: AppFooterProps): React.ReactElement {
  const footerGroups = getFooterGroups(showGettingStartedLink);

  return (
    <footer className="app-footer bsui-scope" data-bs-theme="dark">
      <Container className="app-footer__inner">
        <section className="app-footer__brand" aria-label="BotSpot">
          <h2>BotSpot</h2>
          <p>Research markets, review trades, and work with your broker using AI.</p>
          <div className="app-footer__socials" aria-label="Community and social links">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="app-footer__social-link"
                aria-label={link.label}
                data-platform={link.platform}
              >
                <FontAwesomeIcon icon={link.icon} />
              </a>
            ))}
          </div>
        </section>

        <nav className="app-footer__nav" aria-label="Footer navigation">
          {footerGroups.map((group) => (
            <section className="app-footer__nav-group" key={group.title}>
              <h2>{group.title}</h2>
              <ul>
                {group.links.map((link) => (
                  <li key={isRouteLink(link) ? link.to : link.href}>
                    <FooterNavLink link={link} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
      </Container>

      <Container>
        <p className="app-footer__copyright">BotSpot &copy; 2026</p>
      </Container>
    </footer>
  );
}
